import { useState } from 'react';
import { useAppStore } from '../stores';
import { Household, HouseholdMember, isChildIn } from '../domain';

export const useMembersDialog = (household: Household, onClose: () => void) => {
  const removeMember = useAppStore(state => state.chores.removeMember);
  const setChild = useAppStore(state => state.chores.setChild);
  const isOnline = useAppStore(state => state.chores.syncStatus.isOnline);
  const [removing, setRemoving] = useState<HouseholdMember | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  // The members whose "Child" switch has a change on its way to the server.
  const [changingChild, setChangingChild] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // The display names run parallel to the ids; the owner first, the rest as they joined.
  const members: (HouseholdMember & { isOwner: boolean; isChild: boolean; isChangingChild: boolean })[] = household.member_ids
    .map((id, i) => ({
      id,
      name: household.member_display_names[i] || 'Member',
      isOwner: id === household.owner_id,
      isChild: isChildIn(household, id),
      isChangingChild: changingChild.includes(id),
    }))
    .sort((a, b) => Number(b.isOwner) - Number(a.isOwner));

  const confirmRemove = async () => {
    if (!removing) return;
    setIsRemoving(true);
    setError(null);
    try {
      await removeMember(household.id, removing.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setIsRemoving(false);
      setRemoving(null);
    }
  };

  const changeChild = async (member: HouseholdMember, isChild: boolean) => {
    setError(null);
    setChangingChild(ids => [...ids, member.id]);
    try {
      await setChild(household.id, member.id, isChild);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change who is a child');
    } finally {
      setChangingChild(ids => ids.filter(id => id !== member.id));
    }
  };

  return {
    members,
    isOnline,
    error,
    removing,
    isRemoving,
    askToRemove: (member: HouseholdMember) => { setError(null); setRemoving(member); },
    cancelRemove: () => setRemoving(null),
    confirmRemove,
    changeChild,
    close: () => { setError(null); onClose(); },
  };
};
